if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeMezkuiSizeChoice();
        this.initializeMezkuiVariantFeedback();
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      // Warm only the option the shopper is approaching; consume each response once.
      initializeMezkuiVariantFeedback() {
        if (!this.classList.contains('mezkui-pdp')) return;
        this.mezkuiVariantPreloads = new Map();
        this.mezkuiPrefetchOption = (event) => {
          const label = event.target.closest?.('label[for]');
          const target = label ? this.querySelector(`#${CSS.escape(label.htmlFor)}`) : event.target;
          if (!target?.matches?.('variant-selects input[type="radio"]') || target.checked) return;
          const selects = this.variantSelectors;
          if (!selects?.contains(target)) return;
          const productUrl = target.dataset.productUrl || this.dataset.url;
          if (productUrl !== this.dataset.url) return;
          const groups = Array.from(selects.querySelectorAll('fieldset, .product-form__input--dropdown'));
          const values = groups.map((group) => (group.contains(target) ? target : group.querySelector('input:checked, option:checked'))?.dataset.optionValueId);
          if (values.some((value) => !value)) return;
          const requestUrl = this.buildRequestUrlWithParams(productUrl, values);
          const now = Date.now();
          for (const [key, entry] of this.mezkuiVariantPreloads) {
            if (now - entry.created > 10000) this.mezkuiVariantPreloads.delete(key);
          }
          if (this.mezkuiVariantPreloads.has(requestUrl) || this.mezkuiVariantPreloads.size >= 8) return;
          const promise = fetch(requestUrl, { credentials: 'same-origin' })
            .then((response) => response.ok ? response.text() : null)
            .catch(() => null);
          this.mezkuiVariantPreloads.set(requestUrl, { created: now, promise });
        };
        this.addEventListener('pointerover', this.mezkuiPrefetchOption);
        this.addEventListener('focusin', this.mezkuiPrefetchOption);
      }

      // A size-bearing PDP starts without a choice, even on variant-linked entry.
      initializeMezkuiSizeChoice() {
        if (!this.hasAttribute('data-mezkui-size-required')) return;
        this.mezkuiSizeChoices = new Map();
        this.mezkuiResolvedVariant = null;
        this.dataset.mezkuiSizeReady = 'false';
        this.dataset.mezkuiVariantPending = 'false';
        this.restoreMezkuiSizeChoices(this.variantSelectors);
        this.updateVariantInputs();
        this.mezkuiPageShow = (event) => {
          if (event.persisted) this.initializeMezkuiSizeChoice();
        };
        if (this.mezkuiPageShowHandler) window.removeEventListener('pageshow', this.mezkuiPageShowHandler);
        this.mezkuiPageShowHandler = this.mezkuiPageShow;
        window.addEventListener('pageshow', this.mezkuiPageShowHandler);
        this.productForm?.toggleSubmitButton?.(true, this.dataset.mezkuiSizeMessage);
      }

      restoreMezkuiSizeChoices(selects) {
        if (!this.hasAttribute('data-mezkui-size-required') || !selects) return;
        selects.querySelectorAll('[data-mezkui-size-position]').forEach((group) => {
          const choice = this.mezkuiSizeChoices?.get(group.dataset.mezkuiSizePosition);
          group.querySelectorAll('input[type="radio"], select option').forEach((input) => {
            const selected = choice ? input.dataset.optionValueId === choice.id : input.value === '';
            if (input.tagName === 'OPTION') {
              input.selected = selected;
              input.toggleAttribute('selected', selected);
            } else {
              input.checked = selected;
              input.toggleAttribute('checked', selected);
            }
          });
          const label = group.querySelector('[data-selected-value]');
          if (label && !label.classList.contains('dropdown-swatch')) label.textContent = choice?.value || '';
        });
      }

      mezkuiVariantMatchesSize(variant) {
        if (!variant || !this.mezkuiSizeChoices) return false;
        const groups = this.variantSelectors?.querySelectorAll('[data-mezkui-size-position]') || [];
        return groups.length > 0 && Array.from(groups).every((group) => {
          const position = group.dataset.mezkuiSizePosition;
          const choice = this.mezkuiSizeChoices.get(position);
          return choice && variant.options[Number(position) - 1] === choice.value;
        });
      }

      canSubmitMezkuiSizeChoice() {
        if (!this.hasAttribute('data-mezkui-size-required')) return true;
        return this.dataset.mezkuiSizeReady === 'true' &&
          this.mezkuiResolvedVariant?.available === true &&
          this.mezkuiVariantMatchesSize(this.mezkuiResolvedVariant) &&
          this.productForm?.variantIdInput.value === String(this.mezkuiResolvedVariant.id);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        if (this.mezkuiPageShowHandler) window.removeEventListener('pageshow', this.mezkuiPageShowHandler);
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('pointerover', this.mezkuiPrefetchOption);
        this.removeEventListener('focusin', this.mezkuiPrefetchOption);
        this.mezkuiVariantPreloads?.clear();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (this.hasAttribute('data-mezkui-size-required')) {
          const sizeGroup = target.closest('[data-mezkui-size-position]');
          if (sizeGroup && target.dataset.optionValueId) {
            this.mezkuiSizeChoices.set(sizeGroup.dataset.mezkuiSizePosition, {
              id: target.dataset.optionValueId,
              value: target.value,
            });
          }
          this.restoreMezkuiSizeChoices(this.variantSelectors);
          this.dataset.mezkuiSizeReady = 'false';
          this.mezkuiResolvedVariant = null;
          this.updateVariantInputs();
        }
        this.resetProductFormState();
        if (this.classList.contains('mezkui-pdp')) {
          this.dataset.mezkuiVariantPending = 'true';
          this.productForm?.toggleSubmitButton(true);
          this.productForm?.submitButton?.setAttribute('aria-busy', 'true');
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        const requestSequence = this.mezkuiRequestSequence = (this.mezkuiRequestSequence || 0) + 1;
        const preload = this.mezkuiVariantPreloads?.get(requestUrl);
        this.mezkuiVariantPreloads?.delete(requestUrl);
        const fetchText = () => fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Product options could not load');
            return response.text();
          });
        const responsePromise = preload && Date.now() - preload.created <= 10000
          ? preload.promise.then((text) => text || fetchText())
          : fetchText();

        responsePromise
          .then((responseText) => {
            if (requestSequence !== this.mezkuiRequestSequence || controller.signal.aborted) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            this.dataset.mezkuiVariantPending = 'false';
            this.productForm?.submitButton?.removeAttribute('aria-busy');
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (requestSequence !== this.mezkuiRequestSequence || controller.signal.aborted) return;
            console.error(error);
            this.dataset.mezkuiVariantPending = 'false';
            this.productForm?.submitButton?.removeAttribute('aria-busy');
            if (this.classList.contains('mezkui-pdp')) {
              this.productForm?.toggleSubmitButton(true, this.dataset.mezkuiRetryMessage || 'Choose a size to retry');
              this.productForm?.handleErrorMessage(this.dataset.mezkuiOptionError || 'This option could not load. Please select a size again.');
              if (this.hasAttribute('data-mezkui-size-required')) {
                this.mezkuiSizeChoices.clear();
                this.restoreMezkuiSizeChoices(this.variantSelectors);
              }
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          if (this.classList.contains('mezkui-pdp')) {
            this.preProcessHtmlCallbacks.forEach((callback) => callback(variantSelects));
            // Replace atomically: no hidden duplicate controls or 500 ms cleanup delay.
            this.variantSelectors.replaceWith(document.importNode(variantSelects, true));
            const options = this.querySelector('.mezkui-pdp__options');
            if (options?.querySelector('.mezkui-option-guide')) options.querySelectorAll(':scope > .mezkui-pdp__size-guide').forEach((guide) => guide.remove());
          } else {
            HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          }
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          const requiresSize = this.hasAttribute('data-mezkui-size-required');
          const sizeReady = !requiresSize || this.mezkuiVariantMatchesSize(variant);
          if (requiresSize) {
            this.restoreMezkuiSizeChoices(sourceVariantSelects);
            this.mezkuiResolvedVariant = sizeReady ? variant : null;
            this.dataset.mezkuiSizeReady = String(sizeReady);
          }
          this.pickupAvailability?.update(sizeReady ? variant : null);
          this.updateOptionValues(html);
          this.updateURL(productUrl, sizeReady ? variant?.id : undefined);
          this.updateVariantInputs(sizeReady ? variant?.id : undefined);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            requiresSize
              ? !sizeReady || html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.dataset.mezkuiVariantAvailable !== 'true'
              : html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}




